// Where is the backend?
//
// The UI and the backend are two separate servers. The browser has to be told
// which address to send API calls to - and that address must make sense from
// the VIEWER's computer, not from the machine that happens to be running them.
//
// Hardcoding http://localhost:5000 here breaks the moment someone opens the UI
// from another PC: "localhost" then means THEIR machine, which has no backend.
// So by default we reuse whatever host the page itself was loaded from.

const DEV_UI_PORT = '3000';               // port the React dev server runs on
const BACKEND_PORT = process.env.REACT_APP_API_PORT || '5000';

// Explicit overrides still win, for ngrok / cloud / reverse-proxy setups where
// the backend genuinely lives somewhere else.
const runtimeApiOverride = window.__APP_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || '';
const runtimeWsOverride = window.__APP_WS_BASE_URL || process.env.REACT_APP_WS_BASE_URL || '';

// In a deployed build the backend serves the UI too, so both share one address.
// Only in local development do they sit on different ports.
const isDevServer = () => window.location.port === DEV_UI_PORT;

export const getApiUrl = () => {
  if (runtimeApiOverride) return runtimeApiOverride;
  if (!isDevServer()) return window.location.origin;
  // Same host the page came from (localhost, 10.1.10.93, a laptop's name...),
  // just on the backend's port.
  return `${window.location.protocol}//${window.location.hostname}:${BACKEND_PORT}`;
};

export const getWebSocketUrl = () => {
  if (runtimeWsOverride) return runtimeWsOverride;
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const port = isDevServer() ? BACKEND_PORT : window.location.port;
  return `${protocol}//${window.location.hostname}${port ? `:${port}` : ''}`;
};

export const API_BASE_URL = getApiUrl();
export const WS_BASE_URL = getWebSocketUrl();

// Send the login token on every API call, from one place.
//
// The app makes its API calls with plain fetch() in about eighty places, and
// only a handful of them ever attached an Authorization header. That was fine
// while the backend asked for a token on almost nothing - but the application
// list is now per-user, so the server has to know WHO is asking, on every
// call. Adding the header to eighty call sites by hand would be eighty chances
// to miss one.
//
// So fetch itself is wrapped, once, here. Every existing call site keeps
// working untouched and quietly gains the header.
//
// Only requests to OUR backend are touched. A request to Google, or to any
// other site, goes out exactly as it was written - sending our token to a
// third party would be handing it away.
const isOurApi = (url) => {
  try {
    const target = new URL(url, window.location.origin);
    const ours = new URL(API_BASE_URL, window.location.origin);
    return target.origin === ours.origin && target.pathname.startsWith('/api/');
  } catch (error) {
    return false;
  }
};

// Something else may want to know the session died (App.js sends the user back
// to the login screen rather than leaving them on a page full of errors).
const authFailureHandlers = new Set();
export const onAuthFailure = (handler) => {
  authFailureHandlers.add(handler);
  return () => authFailureHandlers.delete(handler);
};

export function installApiAuth() {
  if (window.__apiAuthInstalled) return;    // React StrictMode mounts twice
  window.__apiAuthInstalled = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input, init = {}) => {
    const url = typeof input === 'string' ? input : (input && input.url) || '';

    if (!isOurApi(url)) return originalFetch(input, init);

    const token = localStorage.getItem('authToken');
    const headers = new Headers(
      (init && init.headers) || (typeof input !== 'string' && input.headers) || {}
    );
    if (token && !headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    const response = await originalFetch(input, { ...init, headers });

    // The token is gone or expired: stop pretending we are logged in.
    if (response.status === 401 && token) {
      localStorage.removeItem('authToken');
      authFailureHandlers.forEach((handler) => {
        try { handler(); } catch (error) { console.error('Auth failure handler threw:', error); }
      });
    }

    return response;
  };
}
